import { StringUtils, ArrayUtils, engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Tweens } from "../Components/Effects/Tweens";
import { Attributes } from "../Utils/Attributes";
import Texture = PIXI.Texture;
import { BaseElement } from "../Components/Elements/Graphics/BaseElement";
import { ImageElement } from "../Components/Elements/Graphics/ImageElement";
import { StageController } from "../Components/StageController";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";

// @ts-ignore
@engineComponent(ComponentTypes.DisplayObject)
export class ExplodeElement extends ContainerElement {
    //  holds particles in pool and reused them
    private _pool: Array<ImageElement>;
    private _count: number = 0;
    private _shown: number = 0;
    private _initExplodeDuration: number = 0;
    private _slowMoDuration: number = 0;
    private _finishDuration: number = 0;
    private _delay: number = 0;
    private _maxRadius: number = 0;
    private _minRadius: number = 0;
    private _width: number = 0;
    private _height: number = 0;
    private _minScale: number = 0.2;
    private _maxScale: number = 1;
    private _baseScale: number = 0;

    public create(node: Element): void {
        super.create(node);

        if (node.hasAttribute(ExplodeElementAttributes.INIT_EXPLODE_DURATION)) {
            this._initExplodeDuration = parseInt(node.getAttribute(ExplodeElementAttributes.INIT_EXPLODE_DURATION));
        }

        if (node.hasAttribute(ExplodeElementAttributes.SLOWMO_DURATION)) {
            this._slowMoDuration = parseInt(node.getAttribute(ExplodeElementAttributes.SLOWMO_DURATION));
        }

        if (node.hasAttribute(ExplodeElementAttributes.FINISH_DURATION)) {
            this._finishDuration = parseInt(node.getAttribute(ExplodeElementAttributes.FINISH_DURATION));
        }

        this._delay = 0;
        if (node.hasAttribute(ExplodeElementAttributes.DELAY)) {
            this._delay = parseInt(node.getAttribute(ExplodeElementAttributes.DELAY));
        }

        if (node.hasAttribute(ExplodeElementAttributes.MIN_RADIUS)) {
            this._minRadius = parseInt(node.getAttribute(ExplodeElementAttributes.MIN_RADIUS));
        }

        if (node.hasAttribute(ExplodeElementAttributes.MAX_RADIUS)) {
            this._maxRadius = parseInt(node.getAttribute(ExplodeElementAttributes.MAX_RADIUS));
        }

        if (node.hasAttribute(ExplodeElementAttributes.MIN_SCALE)) {
            this._minScale = parseFloat(node.getAttribute(ExplodeElementAttributes.MIN_SCALE));
        }

        if (node.hasAttribute(ExplodeElementAttributes.MAX_SCALE)) {
            this._maxScale = parseFloat(node.getAttribute(ExplodeElementAttributes.MAX_SCALE));
        }

        if (node.hasAttribute(ExplodeElementAttributes.BASE_SCALE)) {
            this._baseScale = parseFloat(node.getAttribute(ExplodeElementAttributes.BASE_SCALE));
        }

        if (node.hasAttribute(ExplodeElementAttributes.WIDTH)) {
            this._width = parseInt(node.getAttribute(ExplodeElementAttributes.WIDTH));
        }

        if (node.hasAttribute(ExplodeElementAttributes.HEIGHT)) {
            this._height = parseInt(node.getAttribute(ExplodeElementAttributes.HEIGHT));
        }

        this._shown = this._count;
        if (node.hasAttribute(ExplodeElementAttributes.SHOWN)) {
            this._shown = parseInt(node.getAttribute(ExplodeElementAttributes.SHOWN));
            this.createPool(node);
        }

        if (node.hasAttribute(ExplodeElementAttributes.COUNT)) {
            this._count = parseInt(node.getAttribute(ExplodeElementAttributes.COUNT));
            this.createPool(node);
        }

        // this.setExplodeTweens();

        if (
            node.hasAttribute(ExplodeElementAttributes.START) &&
            StringUtils.parseBoolean(node.getAttribute(ExplodeElementAttributes.START))
        ) {
            if (this._count > this._pool.length) {
                if (DEBUG) {
                    this.log(
                        "not enough explode child elements in pool, count: " +
                            this._count.toString() +
                            " pool: " +
                            this._pool.length.toString(),
                    );
                }
                return;
            }

            if (this._shown > this._count) {
                this._shown = this._count;
            }

            this.hideAllChilds();
            this.setStartPosition();
            this.setExplodeTweens();
        }

        if (
            node.hasAttribute(ExplodeElementAttributes.STOP) &&
            StringUtils.parseBoolean(node.getAttribute(ExplodeElementAttributes.STOP))
        ) {
            this.stopTweens();
        }
    }

    /* CREATING POOL */

    private createPool(node: Element) {
        if (node.hasAttribute(ExplodeElementAttributes.IMAGES)) {
            let imageElements: Array<string> = ArrayUtils.stringArray(
                node.getAttribute(ExplodeElementAttributes.IMAGES),
            );
            let elementsResources: Array<string> = this.prepareResources(imageElements);

            this._pool = [];

            for (let i: number = 0; i < this._count; i++) {
                let imageElement: ImageElement = new ImageElement(
                    StageController.getTextureByResourceId(elementsResources[i]),
                );

                this.addChild(imageElement);
                this._pool.push(imageElement);

                this.childs["Item" + i] = imageElement;
            }
        }
    }

    private prepareResources(imageElements: Array<string>): Array<string> {
        let resources: Array<string> = [];
        for (let i: number = 0; i < this._count; i++) {
            resources.push(imageElements[Math.floor(Math.random() * imageElements.length)]);
        }
        return resources;
    }

    private stopTweens(): void {
        for (let i: number = 0; i < this._count; i++) {
            let imageElement: ImageElement = this._pool[i];
            Tweens.removeTweens(imageElement, "all");
        }
    }

    /* INIT EXPLODE */

    private hideAllChilds(): void {
        for (let i: number = 0; i < this._count; i++) {
            let imageElement: ImageElement = this._pool[i];
            imageElement.visible = false;
        }
    }

    private setStartPosition(): void {
        for (let i: number = 0; i < this._shown; i++) {
            let imageElement: ImageElement = this._pool[i];
            let localBounds: PIXI.Rectangle = imageElement.getLocalBounds();

            Tweens.removeTweens(imageElement, "all");

            imageElement.position.x = Math.floor(Math.random() * this._width);
            imageElement.position.y = Math.floor(Math.random() * this._height);
            imageElement.rotation = this.degrees2radians(Math.random() * 360);
            imageElement.pivot.set(Math.floor(localBounds.width / 2), Math.floor(localBounds.height / 2));
        }
    }

    private setExplodeTweens(): void {
        for (let i: number = 0; i < this._shown; i++) {
            let imageElement: ImageElement = this._pool[i];
            let data: TweensData = new TweensData();

            data.part = 0.6;
            data.duration = this._initExplodeDuration;
            data.delay = this._delay;
            data.angle = (360 / this._shown) * i; //get direction
            data.distance = ExplodeElement.getRandom(this._minRadius, this._maxRadius);
            data.endScale = ExplodeElement.getRandom(this._minScale, this._maxScale) * this._baseScale;
            data.rotationCoef = Math.random() * 80 + 1 - 40;

            imageElement.scale.set(0, 0);
            imageElement.visible = true;

            this.setScaleTween(imageElement, data);
            this.setRotationTween(imageElement, data);
            this.setPositionTween(imageElement, data, this.setSlowMoTweens);
        }
    }

    private setSlowMoTweens(imageElement: ImageElement, data: TweensData): void {
        Tweens.removeTweens(imageElement, "all");

        if (this._slowMoDuration > 0) {
            data.part = 0.7;
            data.delay = 0;
            data.duration = this._slowMoDuration;

            this.setScaleTween(imageElement, data);

            data.part = 0.8;
            this.setRotationTween(imageElement, data);
            this.setPositionTween(imageElement, data, this.setEndTweens);
        } else {
            this.setEndTweens(imageElement, data);
        }
    }

    private setEndTweens(imageElement: ImageElement, data: TweensData): void {
        Tweens.removeTweens(imageElement, "all");

        if (this._finishDuration > 0) {
            data.part = 1;
            data.delay = 0;
            data.duration = this._finishDuration;

            this.setScaleTween(imageElement, data);

            data.part = 3;
            this.setRotationTween(imageElement, data);
            this.setPositionTween(imageElement, data, this.removeTweens);
        } else {
            this.removeTweens(imageElement, data);
        }
    }

    private removeTweens(imageElement: ImageElement, data: TweensData): void {
        Tweens.removeTweens(imageElement, "all");
    }

    private setScaleTween(imageElement: ImageElement, data: TweensData): void {
        let startScale: number = imageElement.scale.x > 0.1 ? imageElement.scale.x : 0.1;
        let scaleTimeline: string = [startScale, data.endScale * data.part].join(";");
        let tweenValues: Array<PIXI.Point> = ExplodeElement._getPointsFromPointsAttribute(
            scaleTimeline,
        ) as Array<PIXI.Point>;
        let tweenEase: string = "linear";
        let tweenDuration: number = data.duration;
        let tweenDelay: number = data.delay;
        let tweenLoop: boolean = false;

        Tweens.scaleTween(imageElement, { tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop });
    }

    private setPositionTween(imageElement: ImageElement, data: TweensData, callback: (...params: any[]) => void): void {
        let newX = data.distance * data.part * Math.cos(this.degrees2radians(data.angle));
        let newY = data.distance * data.part * Math.sin(this.degrees2radians(data.angle));

        let tweenX: Array<number> = [imageElement.position.x, newX];
        let tweenY: Array<number> = [imageElement.position.y, newY];
        let tweenEase: string = "linear";
        let tweenDuration: number = data.duration;
        let tweenDelay: number = data.delay;
        let tweenLoop: boolean = false;

        Tweens.positionTween(imageElement, {
            tweenX,
            tweenY,
            tweenEase,
            tweenDuration,
            tweenDelay,
            tweenLoop,
            callbackFunction: callback,
            callbackParams: [imageElement, data],
            callbackScope: this,
        });
    }

    private setRotationTween(imageElement: ImageElement, data: TweensData): void {
        let rotation: number = ExplodeElement.radians2degrees(imageElement.rotation);
        let degrees: Array<number> = [rotation, rotation + data.rotationCoef * data.part];
        let tweenEase: string = "linear";
        let tweenDuration: number = data.duration;
        let tweenDelay: number = data.delay;
        let tweenLoop: boolean = false;

        Tweens.rotationTween(imageElement, { degrees, tweenEase, tweenDuration, tweenDelay, tweenLoop });
    }

    private static getRandom(min: number, max: number): number {
        return Math.random() * (max - min) + min;
    }

    private static radians2degrees(radians: number): number {
        return radians / (Math.PI / 180);
    }

    clone(): ExplodeElement {
        // custom instead of ContainerElement
        let element: ExplodeElement = new ExplodeElement();

        // super
        element.style = this.style;
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.visible = this.visible;
        element.name = this.name;

        element.childs = {};
        element.removeChildren();
        for (let key in this.childs) {
            element.childs[key] = this.childs[key].clone();
            element.addChild(element.childs[key]);
        }

        if (this.maskSprite != null) {
            let elementMask = new PIXI.Sprite((<PIXI.Sprite>this.maskSprite).texture);
            elementMask.position.x = this.maskSprite.position.x;
            elementMask.position.y = this.maskSprite.position.y;

            element.maskSprite = elementMask;
            element.addChild(elementMask);
            element.mask = elementMask;
        }

        // custom
        element._count = this._count;
        element._initExplodeDuration = this._initExplodeDuration;
        element._slowMoDuration = this._slowMoDuration;
        element._finishDuration = this._finishDuration;
        element._delay = this._delay;
        element._maxRadius = this._maxRadius;
        element._minRadius = this._minRadius;
        element._width = this._width;
        element._height = this._height;
        element._minScale = this._minScale;
        element._maxScale = this._maxScale;
        element._baseScale = this._baseScale;

        element._pool = [];

        for (let n in element.childs) {
            element._pool.push(element.childs[n]);
        }

        // super
        element.markAsCloned();
        return element;
    }
}

export class TweensData {
    public part: number;
    public duration: number;
    public delay: number;
    public distance: number;
    public angle: number;
    public endScale: number;
    public rotationCoef: number;
}

const ExplodeElementAttributes = {
    IMAGES: "Images",
    COUNT: "Count",
    SHOWN: "Shown",
    INIT_EXPLODE_DURATION: "InitExplodeDuration",
    SLOWMO_DURATION: "SlowMoDuration",
    FINISH_DURATION: "FinishDuration",
    DELAY: "Delay",
    MIN_RADIUS: "MinRadius",
    MAX_RADIUS: "MaxRadius",
    WIDTH: "Width",
    HEIGHT: "Height",
    START: "Start",
    STOP: "Stop",
    MIN_SCALE: "MinScale",
    MAX_SCALE: "MaxScale",
    BASE_SCALE: "BaseScale",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/ExplodeElement.ts
 **/